import axios from 'axios';
import store from '../store/store';
import { logout, sessionRenewed } from '../store/authSlice';

// In dev the Vite proxy forwards /api → localhost:8080, so a relative URL
// works without CORS. In prod the backend serves the SPA, so same-origin
// again works with a relative URL. No env var needed.
const api = axios.create({
    baseURL: '/api',
    // 60s: a 5-6 MB CRM upload can exceed 30s on a slow uplink. Run
    // execution overrides this per-call (see commissionReportsApi.executeRun);
    // the effective ceiling for both is the ALB idle timeout, not this value.
    timeout: 60000,
});

// Attach the JWT to every outgoing request.
api.interceptors.request.use((config) => {
    const token = store.getState().auth.accessToken;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/**
 * A 401 used to end the session outright, which is why everyone was thrown
 * back to the sign-in screen every hour: the access token lasts one hour and
 * nothing renewed it. Now a 401 means "try renewing once", and only a failed
 * renewal ends the session.
 *
 * Two details keep this from misbehaving:
 *
 *  - `_retried` marks a request that has already been through here, so a
 *    genuinely unauthorised call cannot bounce between refresh and retry
 *    forever.
 *  - `pendingRefresh` shares one in-flight renewal across every request that
 *    got a 401 at the same moment. Without it a page issuing several calls at
 *    once would fire several renewals, and since renewing ROTATES the token,
 *    the later ones would present an already-spent token — which the server
 *    correctly treats as theft and responds to by killing the whole session.
 *    The bug would look like "signed out at random when a page loads".
 */
let pendingRefresh = null;

function renewSession() {
    if (!pendingRefresh) {
        const refreshToken = store.getState().auth.refreshToken;
        if (!refreshToken) {
            return Promise.reject(new Error('no refresh token'));
        }
        // A bare axios call, not `api`: this must not carry the expired bearer
        // token, and must not recurse through this interceptor.
        pendingRefresh = axios
            .post('/api/auth/refresh', { refreshToken })
            .then((r) => {
                store.dispatch(sessionRenewed(r.data));
                return r.data.accessToken;
            })
            .finally(() => {
                pendingRefresh = null;
            });
    }
    return pendingRefresh;
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const isAuthCall = original?.url?.includes('/auth/');

        // Only a 401 on a normal call is a candidate for renewal. A 401 from
        // the auth endpoints themselves means the credentials were wrong, not
        // that the session aged out.
        if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
            original._retried = true;
            try {
                const accessToken = await renewSession();
                original.headers = { ...original.headers, Authorization: `Bearer ${accessToken}` };
                return api(original);
            } catch {
                store.dispatch(logout());
            }
        } else if (error.response?.status === 401 && (isAuthCall || original?._retried)) {
            store.dispatch(logout());
        }
        return Promise.reject(error);
    },
);

export default api;

// Auth-specific helpers — keep the call sites readable.
export const authApi = {
    register: (email, password) =>
        api.post('/auth/register', { email, password }).then((r) => r.data),
    login: (email, password) =>
        api.post('/auth/login', { email, password }).then((r) => r.data),
    // A wrong current password comes back as 400 (not 401) precisely so the
    // interceptor above doesn't log the user out mid-form.
    changePassword: (currentPassword, newPassword) =>
        api.post('/auth/change-password', { currentPassword, newPassword }),
    // Always 202 — the response never reveals whether the email is registered.
    forgotPassword: (email) =>
        api.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) =>
        api.post('/auth/reset-password', { token, newPassword }),

    // ── Session ──────────────────────────────────────────────────────────
    // Ends the session server-side so the refresh token cannot be used again.
    // Best-effort: a failure here must not stop the user signing out locally.
    signOut: (refreshToken) =>
        axios.post('/api/auth/sign-out', { refreshToken }).catch(() => {}),

    // ── Two-factor authentication ────────────────────────────────────────
    // Second login step. The mfaToken is the opaque challenge from step one —
    // it is not a session and carries no Authorization header.
    verifyMfa: (mfaToken, code) =>
        api.post('/auth/login/mfa', { mfaToken, code }).then((r) => r.data),
    mfaStatus: () => api.get('/auth/mfa').then((r) => r.data),
    mfaSetup: () => api.post('/auth/mfa/setup').then((r) => r.data),
    mfaConfirm: (code) => api.post('/auth/mfa/confirm', { code }).then((r) => r.data),
    mfaDisable: (password) => api.post('/auth/mfa/disable', { password }),
    mfaRegenerateRecoveryCodes: () =>
        api.post('/auth/mfa/recovery-codes').then((r) => r.data),
};
